import './create-managed-fund.scss'

import { notification } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { DatePicker, Form, FormItem, Input, InputNumber, Select, SubmitButton } from 'formik-antd'
import React from 'react'
import { Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../api'
import { filterProps, parseApiErrorResponse } from '../../../components/forms/utils'
import { config } from '../../../config'
import { momentDateHandler } from '../../../date/moment-date-handler'
import { getInterestPaidCodes, InterestPaid } from '../../../models'
import { httpClient } from '../../../util/http-client'
import { messages } from '../messages'
import { CreateManagedFundFormValues, createManagedFundInitialValues } from './model'
import { managedFundValidationSchema } from './validators'

const interestPaidCodes = getInterestPaidCodes()

const { formatIso, newDate } = momentDateHandler

interface CreateManagedFundsProps {
  domain: string
  onSuccess: () => void
}

export const CreateManagedFund = ({ domain, onSuccess }: CreateManagedFundsProps) => {
  const { data: isins } = useSWR('/isins', fetcher)

  const onSubmit = async (
    formValues: CreateManagedFundFormValues,
    { setErrors, setSubmitting, resetForm }: FormikHelpers<CreateManagedFundFormValues>,
  ) => {
    try {
      await httpClient(`/trades/managed-funds`, {
        method: 'POST',
        domain,
        data: convertManagedFundToCreateManagedFundPayload(formValues),
      })

      notification.success({ message: messages.common.success, description: messages.trade.update.success })

      onSuccess()
      resetForm()
    } catch (error) {
      setErrors(parseApiErrorResponse(error))

      notification.error({ message: messages.common.error, description: messages.trade.update.error })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={createManagedFundInitialValues}
      validationSchema={managedFundValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ submitForm }) => (
        <Form name="update-managed-funds">
          <FormItem label="APIR" name="apir" required>
            <Select
              name="apir"
              placeholder="APIR"
              allowClear
              showSearch
              loading={!isins}
              options={isins?.map(({ id, isin }) => ({ label: `${isin} (${id})`, value: isin }))}
              {...filterProps}
            />
          </FormItem>

          <FormItem label="Quantity" name="quantity" required>
            <InputNumber name="quantity" aria-label="quantity" min={0} />
          </FormItem>

          <FormItem label="Unit Price $" name="price" required>
            <InputNumber name="price" aria-label="price" min={0} />
          </FormItem>

          <FormItem label="Purchase Date" name="purchaseDate" required>
            <DatePicker
              name="purchaseDate"
              aria-label="purchaseDate"
              format={config.defaultDateFormat}
              allowClear={false}
            />
          </FormItem>

          <FormItem name="interestPaidCode" label="Interest Paid" required>
            <Select
              name="interestPaidCode"
              placeholder="Select the interest paid"
              showSearch
              options={Object.keys(interestPaidCodes).map((interestPaidCode: InterestPaid) => ({
                label: interestPaidCodes[interestPaidCode],
                value: interestPaidCode,
              }))}
              {...filterProps}
            />
          </FormItem>

          <FormItem label="Allocation Code" name="allocationCode" required>
            <Input name="allocationCode" aria-label="allocationCode" />
          </FormItem>

          <FormItem label="Reference" name="reference">
            <Input name="reference" aria-label="reference" />
          </FormItem>

          <FormItem label="Bank Reference" name="bankReference">
            <Input name="bankReference" aria-label="bankReference" />
          </FormItem>

          <FormItem label="Note" name="note">
            <Input name="note" aria-label="note" />
          </FormItem>

          <FormItem name="actions">
            <Row className="action-buttons">
              <SubmitButton className="update-trade" htmlType="submit" type="primary">
                Create Managed Fund
              </SubmitButton>
            </Row>
          </FormItem>
        </Form>
      )}
    </Formik>
  )
}

export const convertManagedFundToCreateManagedFundPayload = (values: CreateManagedFundFormValues) => {
  const updateTradePayload = { ...values }

  if (updateTradePayload.transactionDate) {
    updateTradePayload.transactionDate = formatIso({ date: newDate(updateTradePayload.transactionDate as Date) })
  }

  return updateTradePayload
}
